import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Breadcrumbs, Link } from '@mui/material';
import { Star as StarIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { useRecipe } from '../../context/RecipeContext';
import mindBackground from '../../images/mind.png';
import midFoodImage from '../../images/mid-food.webp';
import mindDietImage from '../../images/mind-diet.jpg';
import mindCoreFoodsImage from '../../images/mind-core-foods.avif';

const PAGE_URL = 'https://createadish.com/dietary-guide/mind';
const PAGE_TITLE = 'MIND Diet Guide: Brain Health & Cognition - Create A Dish';
const PAGE_DESCRIPTION = 'A hybrid of the Mediterranean and DASH diets specifically designed to boost brain health and prevent cognitive decline through foods like berries, leafy greens, and nuts.';
const PAGE_IMAGE = 'https://createadish.com/images/mind-og.png';

const MIND: React.FC = () => {
  const navigate = useNavigate();
  const { setMode } = useRecipe();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="keywords" content="MIND diet, brain health, cognitive health, memory, alzheimers prevention, neuroprotective diet" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:image" content={PAGE_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "MIND Diet Guide: Brain Health & Cognition",
            "description": PAGE_DESCRIPTION,
            "image": PAGE_IMAGE,
            "author": { "@type": "Organization", "name": "Create A Dish" },
            "publisher": { "@type": "Organization", "name": "Create A Dish", "logo": { "@type": "ImageObject", "url": "https://createadish.com/logo.png" } },
            "datePublished": "2025-12-24",
            "mainEntityOfPage": { "@type": "WebPage", "@id": PAGE_URL },
            "keywords": "BrainHealth, Cognitive, Longevity"
          })}
        </script>
      </Helmet>
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
        <Box sx={{ height: { xs: '40vh', md: '50vh' }, backgroundImage: `url(${mindBackground})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(1px)', position: 'relative' }}>
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0, 0, 0, 0.5)', px: 2, textAlign: 'center' }}>
            <Typography variant="h3" component="h1" sx={{ color: 'white', fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', mb: 2 }}>MIND Diet</Typography>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>Brain Health & Cognition</Typography>
          </Box>
        </Box>
        <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            sx={{ 
              mb: 4,
              '& .MuiBreadcrumbs-separator': {
                color: 'primary.main'
              }
            }}
            aria-label="breadcrumb"
          >
            <Link
              underline="hover"
              onClick={() => navigate('/resources')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Resources
            </Link>
            <Link
              underline="hover"
              onClick={() => navigate('/dietary-guide')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Dietary Guide
            </Link>
            <Typography 
              sx={{ 
                fontSize: '1rem',
                fontWeight: 600,
                color: 'primary.main'
              }}
            >
              MIND Diet
            </Typography>
          </Breadcrumbs>
          <Typography variant="h4" component="h2" sx={{ textAlign: 'center', mb: 4, fontWeight: 'bold', color: 'primary.main' }}>The MIND Diet</Typography>
          <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', maxWidth: '800px', mx: 'auto', lineHeight: 1.6, fontSize: { xs: '1rem', md: '1.1rem' } }}>{PAGE_DESCRIPTION}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4, flexWrap: 'wrap' }}>
            {['#BrainHealth', '#Cognitive', '#Longevity'].map(tag => <Typography key={tag} variant="body2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>{tag}</Typography>)}
          </Box>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
            Welcome to CreateADish.com's guide to the MIND Diet (Mediterranean-DASH Intervention for Neurodegenerative Delay), a hybrid of the Mediterranean and DASH diets specifically designed to boost brain health and prevent cognitive decline through nutrient-rich foods like berries, leafy greens, and nuts.
          </Typography>
          <Box component="img" src={midFoodImage} alt="MIND diet foods" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Historical Background
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The MIND Diet was developed by nutritional epidemiologist Martha Clare Morris and her team at Rush University Medical Center in Chicago, with the first major study published in 2015. It builds on the principles of the Mediterranean and DASH diets, adapting them specifically for brain health based on long-term data from the Rush Memory and Aging Project.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Key Components and Foods
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The MIND Diet scores adherence based on 15 food categories: 10 brain-healthy groups to emphasize and 5 unhealthy ones to limit.
            </Typography>
            
            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Core Foods to Emphasize
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Green Leafy Vegetables</strong>: At least 6 servings/week; spinach, kale, collards.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Other Vegetables</strong>: 1+ serving/day; broccoli, carrots, bell peppers.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Berries</strong>: 2+ servings/week; blueberries, strawberries.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Nuts</strong>: 5+ servings/week; walnuts, almonds.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Whole Grains</strong>: 3+ servings/day; oats, brown rice, quinoa.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Fish</strong>: 1+ meal/week; salmon, tuna.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Olive Oil</strong>: Primary fat source.</Typography>
            </Box>
            <Box component="img" src={mindDietImage} alt="MIND diet components" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Health Benefits
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              Research shows high adherence to the MIND Diet can reduce Alzheimer's risk by 53% and slow cognitive decline by the equivalent of 7.5 years. It supports brain health through anti-inflammatory and antioxidant effects.
            </Typography>
            <Box component="img" src={mindCoreFoodsImage} alt="MIND core foods" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Tips for Adopting the MIND Diet
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Start Simple</strong>: Incorporate one new brain-healthy food weekly, like adding berries to breakfast.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Meal Prep</strong>: Prepare batches of grains and veggies for easy assembly.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Use AI</strong>: Generate recipes on CreateADish.com, such as "MIND Diet berry smoothie."</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Track Adherence</strong>: Aim for high scores in the 15 categories.</Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h5" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>Ready to Create Your Perfect Dish?</Typography>
            <Button variant="contained" size="large" startIcon={<StarIcon />} onClick={() => { setMode('generate'); navigate('/composer'); }} sx={{ minWidth: 200, fontSize: '1.1rem', py: 1.5, px: 4 }}>Get Started</Button>
          </Box>
          <Box sx={{ textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">© 2025 Create A Dish. Made with ❤️ for food lovers everywhere.</Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default MIND;
